<template>
  <div class="media-thumbnail" :class="{ 'media-thumbnail--cropped': isCroppedToMinAspect }" :style="thumbnailStyle">
    <label
      v-if="Object.keys(slots).length"
      :class="{
        'media-thumbnail__checkbox-label': true,
        'media-thumbnail__checkbox-label--covers-entire-thumbnail': props.isInputCoversImage,
      }"
    >
      <slot />
      <span class="media-thumbnail__border-layer"></span>
    </label>

    <IconEmbedded v-if="props.isOpenFullButtonVisible" class="media-thumbnail__fullsize-button" name="maximize_2" @click="reassignClickToImage" />

    <div v-if="checkIsVideo(props.mediaUrl)" class="media-thumbnail__video-wrap">
      <VideoRender :videoId="props.mediaUrl" class="media-thumbnail__video" :customStyles="videoMediaStyle" :customContainerStyles="videoContainerStyle" :showControls="false" showViewerOnClick />
    </div>
    <div v-else class="media-thumbnail__img-wrap">
      <!--Note: src is the thumbnail and then the image display library is smart enough to use the href to pull the full res when showing full-screen-->
      <!-- data-crop-min-aspect tells the lightbox to apply the same centered 4:5 crop we show here -->
      <a
        class="media-thumbnail__img-a"
        lang="en"
        hreflang="en"
        :data-lightbox="props.lightboxKey"
        :data-crop-min-aspect="isCroppedToMinAspect ? props.minAspectRatio : null"
        :href="`${contentBaseUri}/cms/images/expeditions/${props.mediaUrl}`"
      >
        <img class="media-thumbnail__img" :src="`${contentBaseUri}/cms/images/expeditions/thumbnocrop/${props.mediaUrl}`" alt="Photo" ref="refImage" @load="evaluateImageAspect(refImage)" />
      </a>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, useSlots, ref, watch } from "vue";

export interface Props {
  mediaUrl: string;
  isInputCoversImage?: boolean;
  isOpenFullButtonVisible?: boolean;
  lightboxKey?: string;
  imageObjectFit?: "cover" | "contain";
  imageMaxHeight?: string | null;
  containInFixedFrame?: boolean;
  /** When set, taller-than-this photos are center-cropped to this ratio (Meta feed min is 0.81 / 4:5). */
  minAspectRatio?: number | null;
}
const props = withDefaults(defineProps<Props>(), {
  mediaUrl: "",
  isInputCoversImage: false,
  isOpenFullButtonVisible: false,
  lightboxKey: "gallery",
  imageObjectFit: "cover",
  imageMaxHeight: null,
  containInFixedFrame: false,
  minAspectRatio: null,
});

const slots = useSlots();

import FileUtils from "@logic/FileUtils";

// Components
import VideoRender from "@components/VideoRender.vue";
import IconEmbedded from "@components/ui/IconEmbedded.vue";

const contentBaseUri = globalThis.Bootstrap.Config.contentCdnBaseUri;

const mediaFrameHeight = computed(() => {
  if (props.imageObjectFit !== "contain") return "100%";
  return props.containInFixedFrame ? "100%" : "auto";
});

// Opt-in 4:5 crop for social previews: if the photo is taller than minAspectRatio, show a centered crop matching Meta sync.
const croppedForUrl = ref<string | null>(null);
const isCroppedToMinAspect = computed(() => !!props.minAspectRatio && croppedForUrl.value === props.mediaUrl);

function evaluateImageAspect(img: HTMLImageElement | null) {
  if (!props.minAspectRatio) {
    croppedForUrl.value = null;
    return;
  }
  if (!img || !img.naturalWidth || !img.naturalHeight) return;
  const isTall = img.naturalWidth / img.naturalHeight < props.minAspectRatio;
  croppedForUrl.value = isTall ? props.mediaUrl : null;
}

const thumbnailStyle = computed(() => {
  const style: Record<string, string> = {
    "--media-thumbnail-image-fit": isCroppedToMinAspect.value ? "cover" : props.imageObjectFit,
    "--media-thumbnail-image-max-height": props.imageMaxHeight || "none",
    "--media-thumbnail-wrapper-height": mediaFrameHeight.value,
  };
  if (props.minAspectRatio != null) {
    style["--media-thumbnail-crop-aspect-ratio"] = String(props.minAspectRatio);
  }
  return style;
});

const videoContainerStyle = computed(() => {
  return `width: 100%; height: ${mediaFrameHeight.value}; max-height: ${props.imageMaxHeight || "none"};`;
});

const videoMediaStyle = computed(() => {
  return `width: 100%; height: ${mediaFrameHeight.value}; max-height: ${props.imageMaxHeight || "none"}; object-fit: ${props.imageObjectFit};`;
});

function checkIsVideo(assetName: string): boolean {
  return FileUtils.isVideoFileType(assetName, null);
}

// Reassign click to image ====================================================
const refImage = ref<HTMLImageElement | null>(null);

function reassignClickToImage() {
  refImage.value?.click();
}

onMounted(() => {
  evaluateImageAspect(refImage.value);
});

watch(() => props.minAspectRatio, () => evaluateImageAspect(refImage.value));
</script>

<style scoped lang="scss">
// Media thumbnail checkbox ===================================================
.media-thumbnail {
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  z-index: 0;

  &__checkbox-label {
    width: 36px;
    height: 36px;
    display: flex;
    justify-content: center;
    align-items: center;
    position: absolute;
    inset: 0 auto auto 0;
    z-index: 3;
    cursor: pointer;
    user-select: none;

    &--covers-entire-thumbnail {
      width: 100%;
      height: 100%;
    }

    &::before {
      content: "";
      width: 31px;
      height: 31px;
      border-radius: 3px;
      position: absolute;
      inset: 5px auto auto 5px;
      z-index: 2;
      background: rgba(255, 255, 255, 0.5);
      //backdrop-filter: blur(10px); // Caused issues when lots of photos
      cursor: pointer;
      user-select: none;
    }

    :deep(input) {
      width: 15px;
      height: 15px;
      position: absolute;
      inset: 13px auto auto 13px;
      z-index: 3;
      cursor: pointer;
    }
  }

  &__fullsize-button {
    width: 31px;
    height: 31px;
    border-radius: 3px;
    display: flex;
    justify-content: center;
    align-items: center;
    position: absolute;
    inset: 5px 5px auto auto;
    z-index: 4;
    transform: scale(1);
    color: #fff;
    font-size: 23px;
    line-height: 23px;
    //background: rgba(0, 0, 0, 0.3);
    //backdrop-filter: blur(10px); // Caused issues when lots of photos
    transition: transform 0.07s ease-in-out;
    cursor: pointer;

    &:hover {
      transform: scale(1.2);
    }
  }

  &__video-wrap {
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  &__video {
    width: 100%;
  }

  &__img-wrap {
    width: 100%;
    height: var(--media-thumbnail-wrapper-height, 100%);
  }

  &__img-a {
    width: 100%;
    height: var(--media-thumbnail-wrapper-height, 100%);
    display: flex;
    justify-content: center;
    align-items: center;
  }

  &__img {
    width: 100%;
    height: var(--media-thumbnail-wrapper-height, 100%);
    max-height: var(--media-thumbnail-image-max-height, none);
    margin: 0;
    object-fit: var(--media-thumbnail-image-fit, cover);
  }

  // Centered crop viewport for photos taller than minAspectRatio (keeps the 250px height budget).
  &--cropped {
    .media-thumbnail__img-wrap {
      display: flex;
      justify-content: center;
    }

    .media-thumbnail__img-a {
      aspect-ratio: var(--media-thumbnail-crop-aspect-ratio);
      height: var(--media-thumbnail-image-max-height);
      width: auto;
      max-width: 100%;
    }

    .media-thumbnail__img {
      width: 100%;
      height: 100%;
      max-height: none;
    }
  }
}
</style>
